import { ref } from 'vue';
import { toast } from 'vue-sonner';
import useApi from '@/composables/useApi.ts';
import useApp from '@/composables/useApp.ts';
import type { Element } from '@/types';

let synced = false;

const favorites = ref(new Set<string>());
let previousState: Set<string> | null = null;

function saveState() {
    previousState = new Set(favorites.value);
}

function undo() {
    if (previousState) {
        favorites.value = previousState;
        previousState = null;
    }
}

function isFavorite(uid: string) {
    return favorites.value.has(uid);
}

export default function useFavorites() {
    const app = useApp();
    const useFetch = useApi();

    if (!synced) {
        favorites.value = new Set(
            app.favorites.map((favorite) => favorite.uid)
        );
        synced = true;
    }

    async function addDocument(uid: string, showToast: boolean = true) {
        saveState();
        favorites.value.add(uid);

        const { data, error } = await useFetch<Element[]>(
            'favorite/document'
        ).post({
            uid,
        });

        if (error.value) {
            toast.error('Dokument konnte nicht gemerkt werden.');
            console.error(error.value);
            undo();
            return;
        }

        app.favorites = data.value as Element[];
        showToast && toast.success('Auf Merkliste gespeichert.');
    }

    async function removeDocument(uid: string, showToast: boolean = true) {
        saveState();
        favorites.value.delete(uid);

        const { data, error } = await useFetch<Element[]>(
            'unfavorite/document'
        ).post({
            uid,
        });

        if (error.value) {
            toast.error(
                'Dokumente konnte nicht aus der Merkliste entfernt werden.'
            );
            console.error(error.value);
            undo();
            return;
        }

        app.favorites = data.value as Element[];
        showToast &&
            toast('Aus der Merkliste entfernt.', {
                action: {
                    label: 'Rückgängig',
                    onClick: () => addDocument(uid, false),
                },
            });
    }

    async function toggleDocument(uid: string, showToast: boolean = true) {
        if (isFavorite(uid)) {
            await removeDocument(uid, showToast);
        } else {
            await addDocument(uid, showToast);
        }
    }

    async function addEntry(uid: string, showToast: boolean = true) {
        saveState();
        favorites.value.add(uid);

        const { data, error } = await useFetch<Element[]>(
            'favorite/entry'
        ).post({
            uid,
        });

        if (error.value) {
            toast.error('Seite konnte nicht gemerkt werden.');
            console.error(error.value);
            undo();
            return;
        }

        app.favorites = data.value as Element[];
        showToast && toast.success('Auf Merkliste gespeichert.');
    }

    async function removeEntry(uid: string, showToast: boolean = true) {
        saveState();
        favorites.value.delete(uid);

        const { data, error } = await useFetch<Element[]>(
            'unfavorite/entry'
        ).post({
            uid,
        });

        if (error.value) {
            toast.error(
                'Seite konnte nicht von der Merkliste entfernt werden.'
            );
            console.error(error.value);
            undo();
            return;
        }

        app.favorites = data.value as Element[];
        showToast &&
            toast('Aus der Merkliste entfernt.', {
                action: {
                    label: 'Rückgängig',
                    onClick: () => addEntry(uid, false),
                },
            });
    }

    async function toggleEntry(uid: string, showToast: boolean = true) {
        if (isFavorite(uid)) {
            await removeEntry(uid, showToast);
        } else {
            await addEntry(uid, showToast);
        }
    }

    return {
        addDocument,
        removeDocument,
        toggleDocument,

        addEntry,
        removeEntry,
        toggleEntry,

        isFavorite,
    };
}
